import React, { useContext, useEffect, useState } from "react";
import { PaymentFrequency } from "../../../../common/interfaces/enums/PaymentFrequency";
import ProductType from "../../../../common/interfaces/enums/ProductType";
import { QuoteTableView } from "../../../enums/QuoteTableView";
import SortBy from "../../../enums/SortBy";
import useQuoteSections, {
    QuoteTableCallToActionFunction
} from "../../../hooks/useQuoteSections";
import { IClient } from "../../../../common/interfaces/IClient";
import IQuoteCardResult from "../../../interfaces/results/IQuoteCardResult";
import { QuoteGroupContext } from "../../../providers/RootContextProvider";
import { getProductTypeAsString } from "../../../../common/shared/enumHelpers";
import { QuoteTableInfo } from "../../../types/quoteTable/QuoteTableInfo";
import { QuoteTableSectionInfoCollective } from "../../../types/quoteTable/QuoteTableSectionInfo";
import ContentSlider from "../../ContentSlider/ContentSlider";
import CustomerServiceModal from "../../Modals/CustomerServiceModal/CustomerServiceModal";
import PolicyFeatureRatingsModal from "../../Modals/PolicyFeatureRatingsModal/PolicyFeatureRatingsModal";
import QuoteTableOverview from "./Overview/QuoteTableOverview";
import QuoteTable from "./QuoteTable";
import QuoteTableRightFitCard from "./RightFitCard/QuoteTableRightFitCard";

interface IQuoteTableWrapperProps {
    cards: IQuoteCardResult[];
    sortBy: SortBy;
    clients: IClient[];
    handlePaymentFrequencyChange: (paymentFreq: PaymentFrequency) => Promise<void>;
    handleClickCallToAction: QuoteTableCallToActionFunction;
    isAdmin?: boolean;
    isDisabled?: boolean;
}

const QuoteTableWrapper: React.FC<IQuoteTableWrapperProps> = (props) => {
    const {
        cards,
        sortBy,
        clients,
        handlePaymentFrequencyChange,
        handleClickCallToAction,
        isAdmin,
        isDisabled
    } = props;
    const hasSelection = cards.some((s) => s.state.isSelected);
    const { selectors, actions } = useContext(QuoteGroupContext);
    const groupId = selectors.getGroupId();
    const currentView = selectors.getCurrentTableView();
    // Local State Data
    const [isOpenCustomerServiceModal, setIsOpenCustomerServiceModal] = useState(false);
    const [isOpenPolicyFeaturesModal, setIsOpenPolicyFeaturesModal] = useState(false);

    const {
        getQuoteTableHeaderInfo,
        getQuoteTableHeaderStickyInfo,
        getInsurerInfo,
        getOffersAndBenefitsInfo,
        getProductInfo,
        getFinancialStrengthInfo,
        getCustomerServiceInfo,
        // getPolicyFeaturesInfo,
        getFeatureSectionInfoList,
        getDocumentInfo,
        getFooterInfo,
        getProductDetailsList,
        setNewGlobalState,
        globalState,
        createQuoteTableState
    } = useQuoteSections(groupId, {
        hasSelection,
        isDisabled,
        isAdmin
    });

    useEffect(() => {
        setNewGlobalState({
            hasSelection,
            isDisabled,
            isAdmin: isAdmin
        });
    }, [hasSelection, isDisabled, isAdmin]);

    /**
     *
     * Modal Handlers
     *
     */

    const handleClickCustomerServiceModal = (): void => {
        setIsOpenCustomerServiceModal(true);
    };

    /**
     *
     * Model Construction
     *
     */

    const getSize = (results: IQuoteCardResult[]): QuoteTableInfo["size"] => {
        const maxResult = Math.max(...results.map((r) => r.result.totalPremium));

        if (maxResult >= 10000) return "extra-large";

        if (maxResult >= 100) return "large";

        return "small";
    };

    const getEndCardContent = (sectionId: string): React.ReactNode => {
        if (sectionId === "Header" || sectionId === "Sticky Header")
            return (
                <QuoteTableRightFitCard isOverviewText={sectionId === "Sticky Header"} />
            );

        return null;
    };

    const getFullModel = (): QuoteTableInfo => {
        const productCollectiveSections: QuoteTableSectionInfoCollective[] = getProductDetailsList(
            cards
        ).map((product) => {
            const insurerInfo =
                product === ProductType.Health && getInsurerInfo(cards, product);

            const collection: QuoteTableSectionInfoCollective = {
                id: product.toString(),
                type: "collective",
                title: getProductTypeAsString(product, false),
                sections: [
                    insurerInfo,
                    getProductInfo(cards, product),
                    ...getFeatureSectionInfoList(
                        cards,
                        product,
                        currentView === QuoteTableView.Details
                    ),
                    getDocumentInfo(cards, product)
                ]
            };

            return collection;
        });

        return {
            size: getSize(cards),
            endCardContent: getEndCardContent,
            sections: [
                getQuoteTableHeaderInfo(
                    cards,
                    sortBy,
                    clients,
                    handleClickCallToAction,
                    handlePaymentFrequencyChange
                ),
                getOffersAndBenefitsInfo(cards, false),
                getFinancialStrengthInfo(cards),
                getCustomerServiceInfo(cards, handleClickCustomerServiceModal),
                ...productCollectiveSections,
                getFooterInfo(
                    cards,
                    handleClickCallToAction,
                    currentView,
                    actions.setCurrentTableView
                )
                //getPolicyFeaturesInfo(results, handleClickPolicyFeaturesModal)
            ]
        };
    };

    const getStickyModel = (): QuoteTableInfo => {
        return {
            size: getSize(cards),
            endCardContent: getEndCardContent,
            sections: [
                getQuoteTableHeaderStickyInfo(
                    cards,
                    sortBy,
                    handleClickCallToAction,
                    handlePaymentFrequencyChange
                )
            ]
        };
    };

    const isOverview = currentView === QuoteTableView.Overview;

    return (
        <React.Fragment>
            <PolicyFeatureRatingsModal
                results={[...cards]} // We shallow clone
                isOpen={isOpenPolicyFeaturesModal}
                onClose={() => setIsOpenPolicyFeaturesModal(false)}
            />
            <CustomerServiceModal
                results={[...cards]} // We shallow clone
                isOpen={isOpenCustomerServiceModal}
                onClose={(): void => {
                    setIsOpenCustomerServiceModal(false);
                }}
            />
            <ContentSlider
                stickyContent={!isOverview && <QuoteTable model={getStickyModel()} />}
                isSizedToParent={isOverview}
                hideNavDesktop>
                {isOverview ? (
                    <QuoteTableOverview
                        groupId={groupId}
                        results={cards.map((result) => {
                            return {
                                result,
                                quoteState: createQuoteTableState(result, globalState)
                            };
                        })}
                        clients={clients}
                        sortBy={sortBy}
                        handleCallToAction={handleClickCallToAction}
                        handlePaymentFrequencyChange={handlePaymentFrequencyChange}
                        setView={actions.setCurrentTableView}
                        isDisabled={isDisabled}
                    />
                ) : (
                    <QuoteTable model={getFullModel()} />
                )}
            </ContentSlider>
        </React.Fragment>
    );
};

export default QuoteTableWrapper;
